import formatCurrency from "./format-currency.helper";
import TransactionType from "@homegame/common/dist/enum/transaction-type.enum";
import TransactionInterface from "@homegame/common/dist/interface/transaction.interface";
import { DebtHistory } from "../../transactions/components/debt-item.component";
import TransactionStatus from "@homegame/common/dist/enum/transaction-status.enum";
import UserInterface from "@homegame/common/dist/interface/user.interface";

type TransactionLike = TransactionInterface | DebtHistory;

export function getTransferContextFromTransaction(tx: TransactionLike): {
  initialDebtor: UserInterface;
  initialCreditor: UserInterface;
  transferor: UserInterface;
  newCreditor: UserInterface;
} {
  const initial = tx.initialTransaction;
  const parents = tx.parents ?? [];
  const results = tx.game?.results ?? [];

  const fallbackTransferor = tx.fromUser!;
  const fallbackCreditor = tx.toUser!;

  if (!initial || !initial.fromUser || !initial.toUser) {
    return {
      initialDebtor: fallbackTransferor,
      initialCreditor: fallbackCreditor,
      transferor: fallbackTransferor,
      newCreditor: fallbackCreditor,
    };
  }

  const initialDebtor = initial.fromUser;
  const initialCreditor = initial.toUser;
  const newCreditor = tx.toUser!;

  const initialDebtorResult =
    results.find((r) => r.playerId === initialDebtor.id)?.result ?? 0;
  const initialCreditorResult =
    results.find((r) => r.playerId === initialCreditor.id)?.result ?? 0;
  const newCreditorResult =
    results.find((r) => r.playerId === newCreditor.id)?.result ?? 0;

  if (newCreditorResult < 0) {
    return {
      initialDebtor,
      initialCreditor,
      transferor: newCreditor,
      newCreditor: initialCreditor,
    };
  }

  if (initialDebtorResult > 0) {
    return {
      initialDebtor,
      initialCreditor,
      transferor: initialDebtor,
      newCreditor,
    };
  }

  if (initialCreditorResult > 0 && newCreditor.id !== initialCreditor.id) {
    return {
      initialDebtor,
      initialCreditor,
      transferor: initialCreditor,
      newCreditor,
    };
  }

  const externalTransfer = parents.find(
    (p) =>
      p.fromUserId !== initialDebtor.id && p.fromUserId !== initialCreditor.id,
  );
  if (externalTransfer?.fromUser) {
    return {
      initialDebtor,
      initialCreditor,
      transferor: externalTransfer.fromUser,
      newCreditor,
    };
  }

  return {
    initialDebtor,
    initialCreditor,
    transferor: fallbackTransferor,
    newCreditor: fallbackCreditor,
  };
}

function renderDebtTransferTextV2(
  tx: TransactionLike,
  viewerId: number,
): string {
  const { initialDebtor, initialCreditor, transferor, newCreditor } =
    getTransferContextFromTransaction(tx);

  const isViewer = (u: UserInterface) => u.id === viewerId;
  const name = (u: UserInterface) => (isViewer(u) ? "You" : u.name);
  const amountStr = `$${tx.amount}`;

  if (
    transferor.id === initialDebtor.id &&
    newCreditor.id !== initialCreditor.id
  ) {
    return `${name(transferor)} transferred a debt of ${amountStr} you owed to ${name(
      initialCreditor,
    )}.`;
  }

  if (
    transferor.id !== initialDebtor.id &&
    transferor.id !== initialCreditor.id
  ) {
    return `${name(transferor)} transferred ${amountStr} to cover ${name(
      initialDebtor,
    )}'s debt to ${name(initialCreditor)}.`;
  }

  if (
    transferor.id === initialCreditor.id &&
    newCreditor.id !== initialCreditor.id
  ) {
    return `${name(transferor)} transferred ${amountStr} of their credit from ${name(
      initialDebtor,
    )} to ${name(newCreditor)}.`;
  }

  return `${name(transferor)} transferred ${amountStr} of debt.`;
}

export const getTransactionText = (
  tx: TransactionLike,
  currentUserId?: number,
  options?: { debug?: boolean },
): string | JSX.Element => {
  const { type, status, amount, currency, fromUser, toUser, parents } = tx;

  const isMe = (id?: number) => (id === currentUserId ? "you" : undefined);
  const nameOrYou = (user: { name: string }, id?: number) =>
    isMe(id) || user.name;

  const formattedAmount = formatCurrency(amount, currency);
  const showDebug = options?.debug;

  const isViewerDebtor = tx.fromUserId === currentUserId;
  const creditorName = tx.toUser?.name || "Unknown";
  const debtorName = tx.fromUser?.name || "Unknown";

  const debtor = fromUser?.name || `User ${tx.fromUserId}`;
  const creditor = toUser?.name || `User ${tx.toUserId}`;

  switch (type) {
    case TransactionType.PAYMENT:
      if (status === TransactionStatus.APPROVED) {
        return isViewerDebtor
          ? `I paid ${creditorName}.${showDebug ? ` GAME ID: ${tx.gameId}, DEBT ID: ${tx.id}` : ""}`
          : `${debtorName} paid me.${showDebug ? ` GAME ID: ${tx.gameId}, DEBT ID: ${tx.id}` : ""}`;
      }
      if (currentUserId === tx.fromUserId)
        return <>Return of debt to {toUser!.name}</>;
      if (currentUserId === tx.toUserId)
        return <>Return of debt from {fromUser!.name}</>;
      return (
        <>
          Return of debt from {nameOrYou(fromUser!, tx.fromUserId)} to{" "}
          {toUser!.name}
        </>
      );

    case TransactionType.BUY_IN:
      if (currentUserId)
        return (
          <>
            <span style={{ color: "#d95e66" }}>Buy-in</span>
          </>
        );
      return (
        <>
          <span style={{ color: "#d95e66" }}>Buy-in</span> from{" "}
          {nameOrYou(fromUser!, tx.fromUserId)}
        </>
      );

    case TransactionType.CASH_OUT:
      if (currentUserId)
        return (
          <>
            <span style={{ color: "#579880" }}>Cashout</span>
          </>
        );
      return (
        <>
          <span style={{ color: "#579880" }}>Cashout</span> from{" "}
          {nameOrYou(toUser!, tx.toUserId)}
        </>
      );

    case TransactionType.DEBT:
      if (isViewerDebtor)
        return (
          <>
            You owe {toUser!.name} {formattedAmount}
          </>
        );
      return (
        <>
          {fromUser!.name} owes you {formattedAmount}
        </>
      );

    case TransactionType.DEBT_TRANSFER: {
      return renderDebtTransferTextV2(tx, currentUserId!);
    }

    case TransactionType.GAME_OFFSET:
      const fullyPaid = parents?.length && parents[0].amount === amount;
      if (isViewerDebtor) {
        return (
          <>
            Debt to {toUser!.name} of {formattedAmount} settled{" "}
            {fullyPaid ? "" : "partially"} through a win
          </>
        );
      }
      if (currentUserId === tx.toUserId) {
        return (
          <>
            Debt from {fromUser!.name} of {formattedAmount} settled{" "}
            {fullyPaid ? "" : "partially"} through a win
          </>
        );
      }
      return `Offset between ${debtor} and ${creditor}`;

    default:
      return (
        <>
          Transaction <b>{type}</b> from {debtor} to {creditor}
        </>
      );
  }
};
